import type { Key9Puzzle } from '../types/puzzle';
import { createFullNotes } from './fullSudoku';
import { isSameDailyPuzzleIdentity, type DailyPuzzleIdentity } from './puzzleIdentity';

export interface ClassicProgress {
  version: 1;
  identity: DailyPuzzleIdentity;
  phase: 'full_in_progress' | 'full_done';
  board: string[];
  notes: string[];
  hints: number;
  seconds: number;
}

export function shouldOfferReturnShortcut(phase: ClassicProgress['phase'] | 'loading', standalone: boolean): boolean {
  return phase === 'full_done' && !standalone;
}

export function hasClassicWork(puzzle: Key9Puzzle, progress: ClassicProgress): boolean {
  return progress.hints > 0 || progress.board.some((digit, index) =>
    /[0.]/.test(puzzle.puzzle[index]) && (digit !== '0' || progress.notes[index] !== ''));
}

export function getClassicPlayCopy(puzzle: Key9Puzzle, progress: ClassicProgress,
  state: { resumed: boolean; saved: boolean; conflict: boolean }) {
  const active = progress.phase === 'full_in_progress';
  const work = hasClassicWork(puzzle, progress);
  return {
    title: !active ? 'Solved for today' : state.resumed && work ? 'Continue your Sudoku' : 'Today’s Sudoku',
    footer: active && work && state.saved && !state.conflict ? 'Saved in this browser' : 'At your pace.'
  };
}

export function classicStorageKey(identity: DailyPuzzleIdentity): string {
  return `daysudoku:classic:v1:${identity.dailyDate}:${identity.puzzleHash}`;
}

export function freshClassicProgress(puzzle: Key9Puzzle, identity: DailyPuzzleIdentity): ClassicProgress {
  return { version: 1, identity: { ...identity }, phase: 'full_in_progress', board: puzzle.puzzle.replace(/\./g, '0').split(''), notes: createFullNotes(), hints: 0, seconds: 0 };
}

export function normalizeClassicProgress(raw: unknown, puzzle: Key9Puzzle, identity: DailyPuzzleIdentity): ClassicProgress | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as ClassicProgress;
  if (value.version !== 1 || !isSameDailyPuzzleIdentity(value.identity, identity)) return null;
  if (!['full_in_progress', 'full_done'].includes(value.phase)) return null;
  if (!Array.isArray(value.board) || value.board.length !== 81 || value.board.some((digit, i) =>
    typeof digit !== 'string' || !/^[0-9]$/.test(digit) || (/[1-9]/.test(puzzle.puzzle[i]) && digit !== puzzle.puzzle[i]))) return null;
  if (![value.hints, value.seconds].every((n) => Number.isFinite(n) && n >= 0)) return null;
  if (value.notes != null && (!Array.isArray(value.notes) || value.notes.some((note) => typeof note !== 'string'))) return null;
  const complete = value.board.join('') === puzzle.solution;
  if (value.phase === 'full_done' && !complete) return null;
  return {
    version: 1, identity: { ...identity }, phase: complete ? 'full_done' : 'full_in_progress',
    board: [...value.board], notes: createFullNotes(value.notes).map((note, i) => value.board[i] === '0' ? note : ''),
    hints: Math.floor(value.hints), seconds: Math.floor(value.seconds)
  };
}

// Only a matching, valid full board can migrate. Key9 results alone are not daily completions.
export function migrateClassicProgress(raw: unknown, puzzle: Key9Puzzle, identity: DailyPuzzleIdentity): ClassicProgress | null {
  if (!raw || typeof raw !== 'object') return null;
  const old = raw as Record<string, unknown>;
  if (!['full_in_progress', 'full_done'].includes(String(old.phase))) return null;
  return normalizeClassicProgress({ version: 1, identity: old.identity, phase: old.phase, board: old.fullBoard,
    notes: old.fullNotes, hints: old.fullHints ?? 0, seconds: old.fullSeconds ?? 0 }, puzzle, identity);
}

export function formatSolveTime(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(total / 3600);
  return `${hours ? `${hours}:` : ''}${String(Math.floor(total / 60) % 60).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

export function classicShareText(progress: ClassicProgress): string {
  return `DaySudoku · ${progress.identity.dailyDate}\nOne Sudoku a Day.\n\n${progress.phase === 'full_done' ? '✓ Solved for today' : 'Today’s puzzle'} · ${formatSolveTime(progress.seconds)}\n${progress.hints === 0 ? 'Without hints' : `Hints ${progress.hints}`}\n\n${progress.phase === 'full_done' ? '🟩🟩🟩\n🟩🟩🟩\n🟩🟩🟩\n\n' : ''}daysudoku.com`;
}
